import { useEffect, useState } from 'react';

/**
 * Tokens for "only the newest request may land".
 *
 * A list that refetches on every keystroke or live update can have several requests in
 * flight, and the network does not answer them in order. Without this a slow answer for
 * "ab" arrived after the answer for "abc" and replaced it, so the list no longer matched the
 * filter on screen.
 */
export interface LatestRequest {
  /** Start a request; the token it returns is current until the next `begin` or `cancel`. */
  begin(): number;
  isCurrent(token: number): boolean;
  /** Make every outstanding token stale, e.g. on unmount. */
  cancel(): void;
}

export function createLatestRequest(): LatestRequest {
  let current = 0;
  return {
    begin() {
      current += 1;
      return current;
    },
    isCurrent(token) {
      return token === current;
    },
    cancel() {
      current += 1;
    },
  };
}

/** `value`, once it has stopped changing for `delayMs`. Typing fires one request, not one per key. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}
